import { collection, addDoc, serverTimestamp, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { AuditAction, AuditSeverity } from './auditLogger';

interface Alert {
  type: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  action: AuditAction;
  userId?: string;
  userEmail?: string;
  details?: Record<string, any>;
  timestamp: any;
  read: boolean;
}

class AlertSystem {
  /**
   * Crear una alerta en Firestore
   */
  async createAlert(
    type: 'critical' | 'warning' | 'info',
    title: string,
    message: string,
    action: AuditAction,
    options: {
      userId?: string;
      userEmail?: string;
      details?: Record<string, any>;
    } = {}
  ): Promise<void> {
    try {
      await addDoc(collection(db, 'alerts'), {
        type,
        title,
        message,
        action,
        userId: options.userId,
        userEmail: options.userEmail,
        details: options.details,
        timestamp: serverTimestamp(),
        read: false,
      } as Alert);
    } catch (error) {
    }
  }

  /**
   * Verificar si debe crear alerta basado en el log de auditoría
   */
  async checkAndCreateAlert(
    action: AuditAction,
    severity: AuditSeverity,
    userId: string,
    options: {
      userEmail?: string;
      resourceType?: string;
      resourceId?: string;
      familyId?: string;
      details?: Record<string, any>;
    } = {}
  ): Promise<void> {
    // Solo crear alertas para eventos críticos o de advertencia
    if (severity !== AuditSeverity.CRITICAL && severity !== AuditSeverity.WARNING) {
      return;
    }

    let title = '';
    let message = '';
    let type: 'critical' | 'warning' | 'info' = 'info';

    switch (action) {
      case AuditAction.UNAUTHORIZED_ACCESS_ATTEMPT:
        type = 'critical';
        title = '🚨 Intento de Acceso No Autorizado';
        message = `Usuario ${options.userEmail || userId} intentó acceder a un recurso sin permisos`;
        break;

      case AuditAction.RATE_LIMIT_EXCEEDED:
        type = 'warning';
        title = '⚠️ Límite de Operaciones Excedido';
        message = `Usuario ${options.userEmail || userId} excedió el límite de operaciones`;
        break;

      case AuditAction.FAMILY_DELETED:
        type = 'critical';
        title = '🗑️ Familia Eliminada';
        message = `Familia "${options.details?.familyName || 'N/A'}" fue eliminada por ${options.userEmail || userId}`;
        break;

      case AuditAction.MEMBER_PROMOTED_TO_ADMIN:
        type = 'warning';
        title = '👑 Nuevo Administrador';
        message = `${options.details?.memberName || 'Usuario'} fue promovido a administrador`;
        break;

      case AuditAction.MEMBER_DEMOTED_FROM_ADMIN:
        type = 'warning';
        title = '👤 Admin Degradado';
        message = `${options.details?.memberName || 'Usuario'} fue removido como administrador`;
        break;

      case AuditAction.ADMIN_SETTINGS_CHANGED:
        type = 'warning';
        title = '⚙️ Configuración Modificada';
        message = `Configuración global del sistema fue modificada por ${options.userEmail || userId}`;
        break;

      case AuditAction.PLAN_CHANGED:
        type = 'warning';
        title = '📋 Plan Modificado';
        message = `Plan cambió de ${options.details?.oldPlan} a ${options.details?.newPlan}`;
        break;

      case AuditAction.SESSION_EXPIRED:
        // No crear alerta para sesiones expiradas (muy común)
        return;

      default:
        // No crear alerta para otras acciones
        return;
    }

    await this.createAlert(type, title, message, action, {
      userId,
      userEmail: options.userEmail,
      details: options.details,
    });
  }

  /**
   * Obtener alertas no leídas
   */
  async getUnreadAlerts(): Promise<Alert[]> {
    try {
      const q = query(
        collection(db, 'alerts'),
        where('read', '==', false),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
    } catch (error) {
      return [];
    }
  }

  /**
   * Verificar patrones sospechosos
   */
  async checkSuspiciousPatterns(userId: string): Promise<void> {
    try {
      // Verificar múltiples intentos de acceso no autorizado
      const recentTime = new Date();
      recentTime.setMinutes(recentTime.getMinutes() - 5);

      const q = query(
        collection(db, 'auditLogs'),
        where('userId', '==', userId),
        where('action', '==', AuditAction.UNAUTHORIZED_ACCESS_ATTEMPT),
        where('timestamp', '>=', recentTime)
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.size >= 3) {
        await this.createAlert(
          'critical',
          '🚨 Actividad Sospechosa Detectada',
          `Usuario ${userId} ha realizado ${snapshot.size} intentos de acceso no autorizado en los últimos 5 minutos`,
          AuditAction.UNAUTHORIZED_ACCESS_ATTEMPT,
          { userId }
        );
      }
    } catch (error) {
    }
  }
}

// Instancia singleton
const alertSystem = new AlertSystem();

export default alertSystem;
